import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { applicantRequirementsApi } from '../applicantsApi';
import { APPLICANT_REQUIREMENT_AUDIT_ENTITY_TYPES, type ApplicantRequirementListItem } from '../applicantTypes';
import { ApplicantRequirementModal } from '../components/ApplicantRequirementModal';
import { APPLICANT_REQUIREMENTS_PATH, RECRUITMENT_SETTINGS_PATH } from '../recruitmentPaths';

/**
 * What an applicant is expected to submit — reached through Recruitment › Settings, never as a
 * sidebar row of its own. The live rows are the checklist every Initial Screening ticks off.
 */
export function ApplicantRequirementsIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [editing, setEditing] = useState<ApplicantRequirementListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore';
    requirement: ApplicantRequirementListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRequirements = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      applicantRequirementsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (requirement: ApplicantRequirementListItem): MenuItem[] => [
      {
        key: `restore-${requirement.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'applicant-requirements.delete',
        disabledReason: requirement.isActive ? 'This requirement is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', requirement }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await applicantRequirementsApi.remove(confirming.requirement.id);
      else await applicantRequirementsApi.restore(confirming.requirement.id);

      toast.success(`${confirming.kind === 'delete' ? 'Deleted' : 'Restored'} ${confirming.requirement.name}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<ApplicantRequirementListItem>
        module="applicant-requirements"
        moduleNoun="applicant requirements"
        breadcrumb={[
          'Human Resources',
          'Recruitment',
          { label: 'Settings', to: RECRUITMENT_SETTINGS_PATH },
          'Applicant Requirements',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={APPLICANT_REQUIREMENT_AUDIT_ENTITY_TYPES}
        rowKey={(requirement) => requirement.id}
        fetch={fetchRequirements}
        reloadKey={reloadKey}
        searchPlaceholder="Search requirements"
        emptyMessage="No requirements yet. Add what an applicant submits — Personal Data Sheet, Transcript of Records — and each initial screening ticks them off."
        onCreate={() => setCreating(true)}
        createPermission="applicant-requirements.create"
        onRowOpen={(requirement) => navigate(`${APPLICANT_REQUIREMENTS_PATH}/${requirement.id}`)}
        openPermission="applicant-requirements.read"
        rowActions={rowActions}
        onEdit={(requirement) => setEditing(requirement)}
        editPermission="applicant-requirements.update"
        onDelete={(requirement) => setConfirming({ kind: 'delete', requirement })}
        deletePermission="applicant-requirements.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(requirement) => (requirement.isActive ? null : 'This requirement is already deleted.')}
        onClearData={() => applicantRequirementsApi.clear()}
        clearDataPermission="applicant-requirements.clear-data"
        otherInfo={(requirement) => requirement}
      />

      {creating || editing !== null ? (
        <ApplicantRequirementModal
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete applicant requirement' : 'Restore applicant requirement'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`applicant-requirement-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `"${confirming.requirement.name}" stops being offered on the initial screening checklist.`
                  : `"${confirming.requirement.name}" is offered again on the initial screening checklist.`}
              </p>

              {confirming.kind === 'delete' && confirming.requirement.checkedCount > 0 ? (
                <p>
                  {confirming.requirement.checkedCount}{' '}
                  {confirming.requirement.checkedCount === 1 ? 'applicant has' : 'applicants have'} it ticked. Those
                  ticks are kept — deleting a requirement does not unrecord what was submitted.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted requirement can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<ApplicantRequirementListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 260,
    flex: true,
    sortable: true,
    render: (requirement) => <TextCell value={requirement.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (requirement) => <TextCell value={requirement.description} />,
  },
  {
    key: 'requiresAttachment',
    header: 'Requires attachment',
    width: 150,
    render: (requirement) => <TextCell value={requirement.requiresAttachment ? 'Yes' : 'No'} />,
  },
  {
    // A correlated count of live applicants with it ticked — not sortable.
    key: 'checkedCount',
    header: 'Submitted by',
    width: 110,
    align: 'right',
    summary: true,
    render: (requirement) => <TextCell value={String(requirement.checkedCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (requirement) =>
      requirement.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
